import {
	ChevronLeftIcon,
	ChevronRightIcon,
	MoreHorizontalIcon
} from 'lucide-react'
import * as React from 'react'
import { cn } from '#lib/utils'
import { Button } from '#ui/button'

function Pagination({ className, ...props }: React.ComponentProps<'nav'>) {
	return (
		<nav
			aria-label='pagination'
			className={cn('mx-auto flex w-full justify-center', className)}
			data-slot='pagination'
			role='navigation'
			{...props}
		/>
	)
}

function PaginationContent({
	className,
	...props
}: React.ComponentProps<'ul'>) {
	return (
		<ul
			className={cn('flex items-center gap-1', className)}
			data-slot='pagination-content'
			{...props}
		/>
	)
}

function PaginationItem({ ...props }: React.ComponentProps<'li'>) {
	return <li data-slot='pagination-item' {...props} />
}

type PaginationLinkProps = {
	isActive?: boolean
} & Pick<React.ComponentProps<typeof Button>, 'size'> &
	React.ComponentProps<'button'>

// Renders as `<button>` rather than the shadcn-default `<a href='#'>`.
// Pagination is a state-change control, not a navigation: an anchor with
// `href='#'` breaks middle-click ("open in new tab" appends `#` to the
// URL) and announces as a link to screen readers. Button is the right
// semantic per WAI-ARIA APG.
function PaginationLink({
	className,
	isActive,
	size = 'icon',
	type = 'button',
	...props
}: PaginationLinkProps) {
	return (
		<Button
			aria-current={isActive ? 'page' : undefined}
			className={cn(className)}
			data-active={isActive}
			data-slot='pagination-link'
			size={size}
			type={type}
			variant={isActive ? 'outline' : 'ghost'}
			{...props}
		/>
	)
}

function PaginationPrevious({
	className,
	text = 'Previous',
	...props
}: React.ComponentProps<typeof PaginationLink> & { text?: string }) {
	return (
		<PaginationLink
			aria-label='Go to previous page'
			className={cn('pl-2!', className)}
			size='default'
			{...props}
		>
			<ChevronLeftIcon data-icon='inline-start' />
			<span className='hidden sm:block'>{text}</span>
		</PaginationLink>
	)
}

function PaginationNext({
	className,
	text = 'Next',
	...props
}: React.ComponentProps<typeof PaginationLink> & { text?: string }) {
	return (
		<PaginationLink
			aria-label='Go to next page'
			className={cn('pr-2!', className)}
			size='default'
			{...props}
		>
			<span className='hidden sm:block'>{text}</span>
			<ChevronRightIcon data-icon='inline-end' />
		</PaginationLink>
	)
}

function PaginationEllipsis({
	className,
	...props
}: React.ComponentProps<'span'>) {
	return (
		<span
			aria-hidden
			className={cn(
				"flex size-9 items-center justify-center [&_svg:not([class*='size-'])]:size-4",
				className
			)}
			data-slot='pagination-ellipsis'
			{...props}
		>
			<MoreHorizontalIcon />
			<span className='sr-only'>More pages</span>
		</span>
	)
}

export {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious
}
